/**
 * JSON-LD Structured Data Component
 * Implements Schema.org markup for SEO using direct DOM manipulation
 */
import { useEffect } from 'react';

const SITE_URL = 'https://bolognacityexperience.com';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_welcome-guests-6/artifacts/wo138l8s_foto%20panorama%20Bologna%20da%20S.Michele%20in%20Bosco.jpg';

// Helper hook to inject JSON-LD into head
const useJsonLd = (id, data) => {
  useEffect(() => {
    if (!data) return;
    
    // Remove existing script with same id
    const existing = document.querySelector(`script[data-jsonld="${id}"]`);
    if (existing) existing.remove();
    
    // Create new script element
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-jsonld', id);
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    
    return () => {
      const el = document.querySelector(`script[data-jsonld="${id}"]`);
      if (el) el.remove();
    };
  }, [id, data]);
};

// Organization Schema - City Red Bus
export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#organization`,
    "name": "City Red Bus Bologna",
    "alternateName": "Bologna City Experience",
    "url": SITE_URL,
    "logo": `${SITE_URL}/logo.png`,
    "image": DEFAULT_IMAGE,
    "description": "Tour operator specializzato in esperienze panoramiche e gastronomiche a Bologna.",
    "telephone": "+39 051 123 4567",
    "email": "info@bolognacityexperience.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Piazza Maggiore",
      "addressLocality": "Bologna",
      "addressRegion": "Emilia-Romagna",
      "postalCode": "40124",
      "addressCountry": "IT"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 44.4937,
      "longitude": 11.3428
    },
    "priceRange": "€€",
    "currenciesAccepted": "EUR",
    "paymentAccepted": "Cash, Credit Card, PayPal"
  };

  useJsonLd('organization', schema);
  return null;
};

// Product Schema for Experience Pages
export const ProductSchema = ({ product, language = 'it' }) => {
  const trans = product?.translations?.find(t => t.language === language) || product?.translations?.[0];
  const adultPrice = product?.prices?.find(p => p.ticket_type === 'adult')?.price || 35;
  
  const productImages = {
    'happy-hour-sunset-tour': DEFAULT_IMAGE,
    'sfoglia-tortellini-experience': 'https://images.unsplash.com/photo-1619528395742-4e41ab87403b?q=80&w=2000'
  };

  const schema = product ? {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": trans?.name || product.id,
    "description": trans?.short_description || trans?.description?.substring(0, 160) || '',
    "image": productImages[product.id] || DEFAULT_IMAGE,
    "brand": {
      "@type": "Brand",
      "name": "City Red Bus Bologna"
    },
    "offers": {
      "@type": "Offer",
      "price": adultPrice,
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "url": `${SITE_URL}/${language}/experience/${product.slug || product.id}`,
      "seller": {
        "@type": "Organization",
        "name": "City Red Bus Bologna"
      }
    }
  } : null;

  useJsonLd(`product-${product?.id}`, schema);
  return null;
};

// Breadcrumb Schema
export const BreadcrumbSchema = ({ items }) => {
  const schema = items?.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url ? `${SITE_URL}${item.url}` : undefined
    }))
  } : null;

  useJsonLd('breadcrumb', schema);
  return null;
};

// FAQ Schema
export const FAQSchema = ({ faqs }) => {
  const schema = faqs?.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  useJsonLd('faq', schema);
  return null;
};

// Website Schema
export const WebsiteSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    "name": "City Red Bus Bologna",
    "alternateName": "Bologna City Experience",
    "url": SITE_URL,
    "inLanguage": ["it", "en", "fr", "de", "es", "pt", "ru", "ja", "nl", "zh", "ko", "pl"],
    "publisher": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`
    }
  };

  useJsonLd('website', schema);
  return null;
};

export default {
  OrganizationSchema,
  ProductSchema,
  BreadcrumbSchema,
  FAQSchema,
  WebsiteSchema
};
